import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  doc, getDoc, collection, onSnapshot,
  query, where
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import BotAssignment from '../components/BotAssignment';
import CommentRulesSection from '../components/CommentRulesSection';
import WhatsAppApiConnect from '../componentes/bot/WhatsAppApiConnect';
import WhatsAppApiSuccessModal from '../componentes/bot/WhatsAppApiSuccessModal';
import WhatsAppEmbeddedSignup from '../componentes/bot/WhatsAppEmbeddedSignup';
import MessengerBusinessLogin from '../componentes/bot/MessengerBusinessLogin';
import { whatsappApi } from '../utils/api';
import PageHeader from '../components/PageHeader';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../context/OrganizationContext';
import { useChannelsOperational } from '../hooks/useChannelOperational';
import './Canales.css';
import { toast } from '../components/Toast';
import { PARAMETRO as PARAMETRO_COEXISTENCIA } from '../utils/coexistenciaC0';
import { textoDeNumeroNoOperativo } from '../utils/numeroNoOperativo';


// Organización dueña de la Meta App. Facebook Login for Business NO deja
// seleccionar páginas del portfolio que posee la app ("This business portfolio
// owns PUX app. You can only select other business portfolios"), así que puxapp
// —que vive ahí— sólo se puede conectar por el flujo clásico. Sin esta salida no
// habría forma de reconectarla desde la UI si su token muere, y puede morir en
// cualquier momento: no caduca por reloj, muere con la sesión del usuario que
// conectó. No afecta a clientes: ellos tienen portfolio propio.
const ORG_DUENA_DE_LA_APP = '9Y8LltUlJhDTEjQufl7H';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || "https://us-central1-sistema-bks.cloudfunctions.net";

export default function Canales() {
  const { context, loading: contextLoading } = useOrganization();
  const [uid, setUid] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [activeTab, setActiveTab] = useState('whatsapp-api');
  const location = useLocation();

  // TODO: Re-enable WhatsApp WEB tab after Meta review

  // WhatsApp state (separado por tipo)
  // `numerosWeb` retirado con `agregarNumero`: se calculaba y no se pintaba.
  // El JSX sólo lista `numerosApi` — los de QR dejaron de mostrarse al pasar a
  // Cloud API, y el estado sobrevivió al cambio de era como las dos funciones.
  const [numerosApi, setNumerosApi] = useState([]);
  const [bots, setBots] = useState([]);
  const [showApiModal, setShowApiModal] = useState(false);
  const [pinModalData, setPinModalData] = useState(null); // { pin, phoneNumber, emailSent } | null

  // Instagram/TikTok/Messenger state (per-account lists for Fase 4 UI; singletons retired)
  const [igAccounts, setIgAccounts] = useState([]);   // per-account IG (multi-cuenta)
  const [messengerAccounts, setMessengerAccounts] = useState([]);   // per-account Messenger (multi-cuenta)
  const [tiktokAccounts, setTiktokAccounts] = useState([]);   // per-account TikTok Business Messaging (multi-cuenta)
  const [disconnectingId, setDisconnectingId] = useState(null);   // accountId in-flight (per-row disconnect)

  // Operational badge (single source of truth): (≥1 channelAccount canal==X estado=='conectado')
  // && X ∈ MESSAGING_CAPABLE. tiktok → false hoy por el gate; undefined → loading (neutro).
  const operational = useChannelsOperational(['instagram', 'messenger', 'tiktok']);

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation('channels');
  const { t: tc } = useTranslation('common');

  // Permission checks based on organization context
  const canManageNumbers = context.member?.role === 'owner' || context.member?.permissions?.canManageNumbers;
  // Canales sociales (IG/TikTok/Messenger): conectar/desconectar = owner o
  // canManageIntegrations. En modo personal el usuario ES el dueño. Coincide con
  // el gate backend (OAuth-start y disconnect exigen canManageIntegrations).
  const canManageIntegrations = context.mode !== 'organization'
    || context.member?.role === 'owner'
    || context.member?.permissions?.canManageIntegrations === true;

  // Read ?tab=<id> from the URL (e.g. OAuth callbacks redirect here with ?tab=tiktok)
  // and strip the query params so a refresh doesn't keep forcing the tab.
  useEffect(() => {
    if (!location.search) return;
    const params = new URLSearchParams(location.search);
    const wantedTab = params.get('tab');
    const validTabs = ['whatsapp-api', 'whatsapp-business', 'instagram', 'tiktok', 'messenger'];
    if (wantedTab && validTabs.includes(wantedTab)) {
      setActiveTab(wantedTab);
    }
    // Mostrar el resultado del callback ANTES de limpiarlo de la URL.
    //
    // Hasta el 07-ago este bloque leía `?error=` sólo para BORRARLO: el callback
    // mandaba un código preciso —tiene diez distintos— y la pantalla no decía nada.
    // El caso que lo puso en evidencia fue un connect de Messenger rechazado porque
    // la página ya pertenecía a otra organización: el sistema sabía exactamente qué
    // pasaba y el usuario vio "no conectado" a secas, con una hora de diagnóstico
    // detrás.
    const err = params.get('error');
    if (err) {
      toast.error(
        t(`oauthErrors.${err}`, {
          cuenta: params.get('cuenta') || '',
          defaultValue: t('oauthErrors.generico', { codigo: err }),
        }),
        // El Toast del repo toma (mensaje, duracionMs). Un objeto acá haría
        // setTimeout(fn, {}) y el aviso desaparecería al instante — justo el
        // mensaje que el usuario necesita leer entero.
        15000
      );
    } else if (params.get('success')) {
      toast.success(t(`oauthSuccess.${params.get('success')}`, {
        defaultValue: t('oauthSuccess.generico'),
      }));
    }

    // Drop ?tab= / ?success= / ?error= from the URL after handling.
    if (params.has('tab') || params.has('success') || params.has('error')) {
      // C0 de coexistence (🟠3 de la revisión de c87a6ab): `?coexistencia=<variante>` es la segunda llave del modo
      // descubrimiento y se leía DESPUÉS de esta limpieza; borrarla aquí convertía el lanzamiento en el normal sin
      // avisar. Se conserva sólo ese parámetro (COEXISTENCE-DISENO.md §6).
      const conservar = new URLSearchParams();
      if (params.has(PARAMETRO_COEXISTENCIA)) conservar.set(PARAMETRO_COEXISTENCIA, params.get(PARAMETRO_COEXISTENCIA));
      const search = conservar.toString();
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    }
    // Only run on initial mount; subsequent tab clicks should not re-trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect #1: Auth State
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        navigate('/');
        return;
      }
      setUid(u.uid);

      // Cargar datos del cliente/organización
      // In org mode, load plan data from organization document
      // In personal mode, load from clientes/{uid}
      const cRef = doc(db, 'clientes', u.uid);
      const cSnap = await getDoc(cRef);
      if (!cSnap.exists()) {
        console.log('Usuario sin plan, redirigiendo a /contratar');
        navigate('/contratar');
        return;
      }

      // Always set base client data; org limits will be loaded in useEffect #2
      setCliente({ id: u.uid, ...cSnap.data() });
      setLoading(false);
    });

    return () => unsubAuth();
  }, [navigate]);

  // useEffect #1.5: Load org limits when in organization mode
  useEffect(() => {
    if (!uid || contextLoading) return;
    if (context.mode === 'organization' && context.orgId) {
      const orgRef = doc(db, 'organizations', context.orgId);
      getDoc(orgRef).then(orgSnap => {
        if (orgSnap.exists()) {
          const orgData = orgSnap.data();
          // Merge org limits/usage/addons into cliente state
          setCliente(prev => ({
            ...prev,
            limits: orgData.limits || prev?.limits,
            usage: orgData.usage || prev?.usage,
            addonsActivos: orgData.addonsActivos || prev?.addonsActivos,
            plan: orgData.planId || orgData.plan || prev?.plan,
            planId: orgData.planId || orgData.plan || prev?.planId,
            featureFlags: orgData.featureFlags || prev?.featureFlags
          }));
        }
      }).catch(err => console.warn('Error loading org limits:', err));
    }
  }, [uid, contextLoading, context.mode, context.orgId]);

  // useEffect #2: Firestore Listeners (SOLO cuando uid está disponible)
  useEffect(() => {
    if (!uid || contextLoading) return;

    console.log('🔗 Setting up Firestore listeners for uid:', uid);

    // Track last snapshot timestamp to prevent duplicate processing during reconnections
    let lastNumerosSnapshotTime = 0;

    // Escuchar números de WhatsApp (separar por tipo) - Query based on organization context
    let numerosQuery;
    if (context.mode === 'organization' && context.orgId) {
      // Org mode: query from organizations/{orgId}/numeros
      numerosQuery = collection(db, 'organizations', context.orgId, 'numeros');
    } else {
      // Personal mode: query from clientes/{uid}/numeros
      numerosQuery = collection(db, 'clientes', uid, 'numeros');
    }

    const unsubNumeros = onSnapshot(
      numerosQuery,
      (qs) => {
        // ⚡ Debounce: ignore rapid-fire snapshots from health checks, but always accept if data changed
        const now = Date.now();
        if (now - lastNumerosSnapshotTime < 100) {
          console.log('⏭️  Skipping duplicate numeros snapshot (rapid fire)');
          return;
        }
        lastNumerosSnapshotTime = now;

        console.log('📱 Numeros updated:', qs.size);
        const allNumeros = qs.docs.map(d => ({ id: d.id, ...d.data() }));

        // Separar por tipoConexion
        setNumerosApi(allNumeros.filter(n => n.tipoConexion === 'api'));

        // AQUÍ HABÍA UN AUTOAJUSTE DE `usage.numerosCount`, y se retiró el 8-oct-2026.
        //
        // Escribía el absoluto `allNumeros.length` sobre el documento del cliente o de la org
        // para corregir el contador. Tres problemas, y los tres juntos lo dejaron inservible:
        //
        //  1. La puerta era `if (cliente && …)`, y este `useEffect` depende de
        //     `[uid, contextLoading, context]`, NO de `cliente`. El callback se quedaba con el
        //     `cliente` del render en que se suscribió, y el primer disparo de `onSnapshot` es
        //     inmediato: si `cliente` aún era `null`, la puerta no se abría — y no volvía a
        //     dispararse, porque `numeros` no cambia al abrir la página.
        //  2. En modo organización el `usage` de la org lo mezcla otro efecto DESPUÉS, así que
        //     ese `cliente` traía el `usage` del documento personal, no el de la org.
        //  3. El `.catch(() => {})` se comía cualquier fallo sin dejar rastro.
        //
        // Resultado: `usage.numerosCount` llevaba **−2** en la organización de PUX con un
        // número conectado, y abrir esta pantalla no lo arreglaba. En vez de arreglar el
        // autoajuste, se quitó el contador de la ecuación: Facturación cuenta los DOCUMENTOS,
        // igual que los topes de verdad (`createNumero.js:94`). Un espejo que hay que mantener
        // sincronizado es una fuente de error; el original no.
      },
      (error) => {
        // Handle Firestore connection errors gracefully
        console.error('❌ Firestore listener error (numeros):', error.code, error.message);
        // Don't crash the app - Firestore will auto-retry
      }
    );

    // Track last snapshot timestamp for bots
    let lastBotsSnapshotTime = 0;

    // Escuchar bots - Query based on organization context
    let botsQuery;
    if (context.mode === 'organization' && context.orgId) {
      // Org mode: query from organizations/{orgId}/bots
      botsQuery = collection(db, 'organizations', context.orgId, 'bots');
    } else {
      // Personal mode: query from clientes/{uid}/bots
      botsQuery = collection(db, 'clientes', uid, 'bots');
    }

    const unsubBots = onSnapshot(
      botsQuery,
      (qs) => {
        // ⚡ Debounce: ignore rapid-fire snapshots from health checks
        const now = Date.now();
        if (now - lastBotsSnapshotTime < 100) {
          console.log('⏭️  Skipping duplicate bots snapshot (rapid fire)');
          return;
        }
        lastBotsSnapshotTime = now;

        console.log('🤖 Bots updated:', qs.size);
        setBots(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.error('❌ Firestore listener error (bots):', error.code, error.message);
      }
    );

    // Escuchar Instagram channelAccounts (per-account / multi-cuenta) — query por canal
    // (El badge "operativo" sale del helper useChannelsOperational; este listener solo
    //  alimenta la LISTA per-cuenta de Fase 4 — singleton integrations/instagram retirado.)
    let igAccountsRef;
    if (context.mode === 'organization' && context.orgId) {
      igAccountsRef = query(collection(db, 'organizations', context.orgId, 'channelAccounts'), where('canal', '==', 'instagram'));
    } else {
      igAccountsRef = query(collection(db, 'clientes', uid, 'channelAccounts'), where('canal', '==', 'instagram'));
    }
    const unsubIgAccounts = onSnapshot(
      igAccountsRef,
      (snap) => setIgAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (error) => console.error('❌ Firestore listener error (instagram channelAccounts):', error.code, error.message)
    );

    // Escuchar TikTok channelAccounts (per-account Business Messaging) — query por canal.
    // (Badge "operativo" del helper; este listener alimenta la LISTA per-cuenta. El render
    //  filtra a source==='business_messaging' para no mostrar la cuenta legacy de Login Kit.)
    let tiktokAccountsRef;
    if (context.mode === 'organization' && context.orgId) {
      tiktokAccountsRef = query(collection(db, 'organizations', context.orgId, 'channelAccounts'), where('canal', '==', 'tiktok'));
    } else {
      tiktokAccountsRef = query(collection(db, 'clientes', uid, 'channelAccounts'), where('canal', '==', 'tiktok'));
    }
    const unsubTiktokAccounts = onSnapshot(
      tiktokAccountsRef,
      (snap) => setTiktokAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (error) => console.error('❌ Firestore listener error (tiktok channelAccounts):', error.code, error.message)
    );

    // Escuchar Messenger channelAccounts (per-account / multi-cuenta) — query por canal
    // (Badge "operativo" del helper; este listener solo alimenta la LISTA Fase 4 —
    //  singleton integrations/messenger retirado.)
    let messengerAccountsRef;
    if (context.mode === 'organization' && context.orgId) {
      messengerAccountsRef = query(collection(db, 'organizations', context.orgId, 'channelAccounts'), where('canal', '==', 'messenger'));
    } else {
      messengerAccountsRef = query(collection(db, 'clientes', uid, 'channelAccounts'), where('canal', '==', 'messenger'));
    }
    const unsubMessengerAccounts = onSnapshot(
      messengerAccountsRef,
      (snap) => setMessengerAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (error) => console.error('❌ Firestore listener error (messenger channelAccounts):', error.code, error.message)
    );

    // ✅ React cleanup ejecuta esto cuando uid o context cambia o componente se desmonta
    return () => {
      console.log('🧹 Cleaning up Firestore listeners for uid:', uid);
      unsubNumeros();
      unsubBots();
      unsubIgAccounts();
      unsubMessengerAccounts();
      unsubTiktokAccounts();
    };
  }, [uid, contextLoading, context]);

  // RETIRADAS el 7-sep-2026: `deleteNumero` (54 líneas) y `agregarNumero` (98).
  //
  // Eran residuo de la era Baileys, cuando el número se escribía a mano y la
  // conexión se hacía por QR. Con Cloud API el botón dice «Conectar WhatsApp
  // API» y el número lo crea el backend cuando Meta responde, así que llevaban
  // meses definidas y sin un solo llamante — ni desde el JSX.
  //
  // No las vio el gate del pre-commit porque `no-unused-vars` está como
  // `warning` y el gate sólo cuenta errores. Las destapó el bundle: ningún
  // literal suyo llegaba al build, porque Terser elimina lo que nadie llama.
  //
  // `/canales` SÍ está viva: es la pantalla principal de conexión —cinco
  // canales, números conectados, asignación de asistente—. Lo que se retira son
  // estas dos funciones, no la pantalla.


  /**
   * Crea el número en el servidor. Lanza con `status` y `detalle` para que el
   * llamante distinga «tope alcanzado» de «ya existe» de «se cayó».
   */

  // Funciones Instagram
  const orgId = context.mode === 'organization' ? context.orgId : null;

  /**
   * Muestra el MOTIVO que devolvió el backend en vez de un aviso genérico.
   *
   * Los `*Start` devuelven ahora un código estable (`not_a_member` |
   * `missing_permission`, ver `integrations/utils/orgAccess.js`) y la frase en
   * `message`. Antes, cualquier respuesta sin `authUrl` caía en el mismo
   * `alert('Error al iniciar conexión con X')`: el sistema sabía exactamente qué
   * pasaba —un permiso que falta— y la pantalla no lo decía. Es el mismo defecto que
   * el bloque de `?error=` de arriba corrigió para los callbacks, en la otra mitad
   * del flujo.
   *
   * Se reusa el namespace `oauthErrors` a propósito: es el que ya mapea los códigos
   * de los callbacks, y el backend emite los mismos.
   */
  const mostrarErrorDeConexion = (provider, data) => {
    const codigo = data?.error;
    toast.error(
      codigo
        ? t(`oauthErrors.${codigo}`, {
            cuenta: data?.cuenta || '',
            defaultValue: t('oauthErrors.generico', { codigo }),
          })
        : t('social.errorConnect', { provider }),
      15000
    );
  };

  const handleConnectInstagram = async () => {
    if (!canManageIntegrations) return;
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(`${API_URL}/api/oauth/instagram/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ uid, organizationId: orgId })
      });
      const data = await res.json().catch(() => ({}));
      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        mostrarErrorDeConexion('Instagram', data);
      }
    } catch (error) {
      console.error('Error connecting Instagram:', error);
      toast.error(t('social.errorConnectRetry', { provider: 'Instagram' }), 15000);
    }
  };

  // (handleDisconnectInstagram retirado: vivía en la rama legacy del singleton, ya
  //  removida. El disconnect per-account es el feature destructivo diferido — ver
  //  MULTICANAL-ESTADO.md.)

  // Funciones TikTok
  const handleConnectTikTok = async () => {
    if (!canManageIntegrations) return;
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(`${API_URL}/integrations_tiktok_messaging_start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ uid, organizationId: orgId })
      });
      const data = await res.json().catch(() => ({}));
      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        mostrarErrorDeConexion('TikTok', data);
      }
    } catch (error) {
      console.error('Error connecting TikTok:', error);
      toast.error(t('social.errorConnectRetry', { provider: 'TikTok' }), 15000);
    }
  };

  // (handleDisconnectTikTok retirado junto con la profile-card legacy de TikTok.)

  // Funciones Messenger
  //
  // SIN USO desde el 07-ago: el botón pasó a <MessengerBusinessLogin>, que usa
  // Facebook Login for Business con config_id y obtiene un token que no expira.
  // Esto se CONSERVA a propósito como camino de vuelta — junto con
  // messengerStart.js y integrations_messenger_callback, que siguen desplegados.
  // Revertir es cambiar el componente por el <button onClick={handleConnectMessenger}>
  // y desplegar hosting: sin tocar functions, sin desconectar nada.
  // Se retira cuando puxapp deje de vivir en el portfolio dueño de la app.
  const handleConnectMessenger = async () => {
    if (!canManageIntegrations) return;
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(`${API_URL}/api/oauth/messenger/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ uid, organizationId: orgId })
      });
      const data = await res.json().catch(() => ({}));
      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        mostrarErrorDeConexion('Messenger', data);
      }
    } catch (error) {
      console.error('Error connecting Messenger:', error);
      toast.error(t('social.errorConnectRetry', { provider: 'Messenger' }), 15000);
    }
  };

  // (handleDisconnectMessenger retirado: vivía en la rama legacy del singleton, ya removida.)

  // Disconnect ONE channel account (per-row). Calls the per-account disconnect callable
  // (rama A) with the channelAccount docId (account.id). On 200 the channelAccounts
  // onSnapshot listener drops the row automatically — no manual refetch needed.
  const handleDisconnectAccount = async (provider, account, accountLabel) => {
    if (!canManageIntegrations) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(t('social.confirmDisconnectAccount', { account: accountLabel }))) return;
    setDisconnectingId(account.id);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(`${API_URL}/api/oauth/${provider}/disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ accountId: account.id, organizationId: orgId })
      });
      if (!res.ok) {
        console.error(`Error disconnecting ${provider} account ${account.id}:`, res.status);
        alert(t('social.errorDisconnect'));
        setDisconnectingId(null);   // re-enable the button so the user can retry
        return;
      }
      // 200 → the channelAccounts onSnapshot listener removes this row automatically.
      // No reset needed: the row unmounts (disconnectingId is harmlessly stale).
    } catch (error) {
      console.error(`Error disconnecting ${provider} account ${account.id}:`, error);
      alert(t('social.errorDisconnect'));
      setDisconnectingId(null);
    }
  };

  // Función para eliminar número API
  const deleteNumeroApi = async (numeroId, phoneNumberId) => {
    try {
      await whatsappApi.disconnect({ numeroId, phoneNumberId, organizationId: orgId });

      // NO SE DECREMENTA AQUÍ (8-oct-2026). Había DOS escritores para el mismo hecho: este
      // `increment(-1)` y el self-heal de arriba, que al dispararse el listener de `numeros`
      // tras el borrado escribe el valor ABSOLUTO. Los dos corrían y se restaba dos veces.
      // Resultado medido en producción: `usage.numerosCount` en **−3** con cero números
      // reales. El `Math.max(0, …)` que había aquí sólo tocaba el estado de React, no
      // Firestore, así que no ponía suelo a nada.
      //
      // Y DESDE EL 8-oct-2026 tampoco se toca el estado local: ya nadie en la interfaz lee
      // `usage.numerosCount`. Facturación cuenta los documentos de `numeros`, y el listener de
      // esta misma pantalla repinta la lista en cuanto llega el snapshot del borrado.
    } catch (error) {
      console.error('Error al desconectar WhatsApp API:', error);
      alert(t('whatsappApi.errorDelete'));
    }
  };

  if (loading || contextLoading) return <div className="loader">{tc('loading')}</div>;
  if (!cliente) return null;

  return (
    <div className="canales-container">
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      {/* Tabs */}
      <div className="canales-tabs">
        <button
          className={`tab-button ${activeTab === 'whatsapp-api' ? 'active' : ''}`}
          onClick={() => setActiveTab('whatsapp-api')}
        >
          {t('tabs.whatsappApi')}
        </button>
        {/* Embedded Signup: default ON — se esconde solo con
            featureFlags.embeddedSignupEnabled === false explícito (kill switch
            por excepción). El flag nunca tuvo writers en código: solo Reviewer
            lo tenía (seteo manual), así que ninguna org creada por el checkout
            lo habría visto jamás (descubierto en el signup E2E, 21-jul-2026). */}
        {cliente?.featureFlags?.embeddedSignupEnabled !== false && (
          <button
            className={`tab-button ${activeTab === 'whatsapp-business' ? 'active' : ''}`}
            onClick={() => setActiveTab('whatsapp-business')}
          >
            {t('tabs.whatsappBusiness')}
          </button>
        )}
        <button
          className={`tab-button ${activeTab === 'instagram' ? 'active' : ''}`}
          onClick={() => setActiveTab('instagram')}
        >
          {t('tabs.instagram')}
        </button>
        <button
          className={`tab-button ${activeTab === 'tiktok' ? 'active' : ''}`}
          onClick={() => setActiveTab('tiktok')}
        >
          {t('tabs.tiktok')}
        </button>
        <button
          className={`tab-button ${activeTab === 'messenger' ? 'active' : ''}`}
          onClick={() => setActiveTab('messenger')}
        >
          {t('tabs.messenger')}
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">

        {activeTab === 'whatsapp-api' && (
          <section className="whatsapp-api-section">
            <h2>{t('whatsappApi.title')}</h2>
            <p style={{ marginBottom: 16, color: '#666' }}>
              {t('whatsappApi.subtitle')}
            </p>

            {canManageNumbers ? (
              <button
                onClick={() => setShowApiModal(true)}
                className="add-btn"
                style={{ marginBottom: 20 }}
              >
                {t('whatsappApi.connect')}
              </button>
            ) : (
              <div style={{
                padding: '1rem',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '6px',
                color: '#856404',
                marginBottom: '1rem'
              }}>
                {t('permissions.noManageNumbersFull')}
              </div>
            )}

            {showApiModal && (
              <WhatsAppApiConnect
                uid={uid}
                onClose={() => setShowApiModal(false)}
                onSuccess={(data) => {
                  setShowApiModal(false);
                  // Real-time listener actualizará automáticamente.
                  // Si el backend devolvió un PIN recién generado, abrimos el modal de éxito.
                  if (data?.pin) {
                    setPinModalData({
                      pin: data.pin,
                      phoneNumber: data.display_phone_number || data.numero || '',
                      emailSent: data.emailSent === true,
                    });
                  }
                }}
              />
            )}

            {pinModalData && (
              <WhatsAppApiSuccessModal
                pin={pinModalData.pin}
                phoneNumber={pinModalData.phoneNumber}
                emailSent={pinModalData.emailSent}
                onClose={() => setPinModalData(null)}
              />
            )}

            <div className="numeros-grid">
              {numerosApi.map(num => {
                const assignedBot = bots.find(b => b.numeroId === num.id);

                return (
                  <div key={num.id} className="numero-card">
                    <p>📱 {num.display_phone_number || num.numero}</p>
                    <p>{t('whatsappApi.phoneNumberId')} {num.phone_number_id}</p>
                    {num.verified_name && <p>{t('whatsappApi.name')} {num.verified_name}</p>}
                    <p>{t('whatsappWeb.status')} <span style={{
                      color: num.estado === 'conectado' ? 'green' :
                             num.estado === 'error_token' ? 'red' :
                             num.estado === 'registro_fallido' ? 'red' :
                             num.estado === 'desconectado' ? 'red' :
                             num.estado === 'en_reconexion' ? '#b45309' :
                             num.estado === 'token_expiring_soon' ? '#b45309' : 'orange',
                      fontWeight: 'bold'
                    }}>
                      {num.estado === 'conectado' ? t('whatsappApi.statusConnected') :
                       num.estado === 'error_token' ? t('whatsappApi.statusTokenError') :
                       num.estado === 'token_expiring_soon' ? t('whatsappApi.statusTokenExpiringSoon') :
                       num.estado === 'webhook_pendiente' ? t('whatsappApi.statusWebhookPending') :
                       /* Los dos estados del alta a medias (paso 6b del callback). Sin
                          estas ramas la cadena cae al `: num.estado` de abajo y le pinta
                          al cliente el literal crudo `pendiente_registro`. */
                       num.estado === 'pendiente_registro' ? t('whatsappApi.statusPendingRegister') :
                       num.estado === 'registro_fallido' ? t('whatsappApi.statusRegisterFailed') :
                       /* C4 (coexistence): lo que dejó account_update. El texto y el motivo salen de
                          textoDeNumeroNoOperativo (función pura, d74); aquí sólo se pinta. */
                       textoDeNumeroNoOperativo(num) ? t(textoDeNumeroNoOperativo(num).badge) :
                       num.estado}
                    </span></p>
                    {/* El motivo, junto al estado. Un alta que Meta rechaza deja el número
                        guardado; sin esto el cliente vería un badge rojo sin saber qué
                        hacer, y el código de Meta sólo estaría en los logs. */}
                    {num.estado === 'registro_fallido' && (
                      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: 10, margin: '8px 0', fontSize: 13, lineHeight: 1.5 }}>
                        {t('whatsappApi.registerFailedHelp')}
                        {num.registroErrorCode && (
                          <div style={{ marginTop: 6, fontFamily: 'monospace', fontSize: 12, color: '#7f1d1d' }}>
                            Meta {num.registroErrorCode}{num.registroError ? ` — ${num.registroError}` : ''}
                          </div>
                        )}
                      </div>
                    )}
                    {/* C4 (coexistence): el motivo en lenguaje simple por reason (decisión de Rodrigo,
                        27-oct), y el código de Meta debajo cuando el motivo es desconocido. */}
                    {textoDeNumeroNoOperativo(num) && (
                      <div data-testid="numero-no-operativo" style={{ background: textoDeNumeroNoOperativo(num).estado === 'desconectado' ? '#fef2f2' : '#fef3c7', border: `1px solid ${textoDeNumeroNoOperativo(num).estado === 'desconectado' ? '#fecaca' : '#fbbf24'}`, borderRadius: 6, padding: 10, margin: '8px 0', fontSize: 13, lineHeight: 1.5 }}>
                        {t(textoDeNumeroNoOperativo(num).motivo, { codigo: textoDeNumeroNoOperativo(num).codigo })}
                      </div>
                    )}
                    <p>{t('whatsappApi.type')}</p>

                    {/* TODO(phase-8): show badge of messages with errorEnvio=whatsapp_api_failed
                        in the last 24h for this number. Needs: (a) composite Firestore index on
                        mensajes collection group by errorEnvio + createdAt, (b) agg endpoint or
                        per-chat listener, (c) UX decision on grouping. Tracked separately. */}
                    {num.estado === 'token_expiring_soon' && (
                      <div style={{
                        backgroundColor: '#fef3c7',
                        border: '1px solid #fbbf24',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        marginBottom: '12px',
                        fontSize: '14px',
                        color: '#78350f',
                      }}>
                        <div style={{ marginBottom: 8 }}>
                          {t('whatsappApi.tokenExpiringSoon', { days: num.daysUntilExpiry || '?' })}
                        </div>
                        {canManageNumbers && (
                          <button
                            onClick={() => setShowApiModal(true)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#e8a000',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: 'bold',
                            }}
                          >
                            {t('whatsappApi.reconnectNow')}
                          </button>
                        )}
                      </div>
                    )}

                    {num.estado === 'conectado' && !assignedBot && (
                      <div style={{
                        backgroundColor: '#fef3c7',
                        border: '1px solid #fbbf24',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        color: '#78350f',
                      }}>
                        <span style={{ fontSize: '18px' }}>⚠️</span>
                        <span>{t('numbers.noBotWarning')}</span>
                      </div>
                    )}

                    <BotAssignment
                      uid={uid}
                      numeroId={num.id}
                      currentBotId={assignedBot?.id}
                    />

                    <div style={{marginTop:8}}>
                      <button
                        className="delete-btn"
                        onClick={() => {
                          if (window.confirm(t('whatsappApi.confirmDelete', { number: num.display_phone_number || num.numero }))) {
                            deleteNumeroApi(num.id, num.phone_number_id);
                          }
                        }}
                      >
                        {t('whatsappWeb.deleteBtn')}
                      </button>
                    </div>
                  </div>
                );
              })}

              {numerosApi.length === 0 && (
                <div style={{
                  padding: 40,
                  textAlign: 'center',
                  color: '#999',
                  gridColumn: '1 / -1'
                }}>
                  <p>{t('whatsappApi.empty')}</p>
                  <p style={{ fontSize: 14, marginTop: 8 }}>
                    {t('whatsappApi.emptyHint')}
                  </p>
                </div>
              )}
            </div>

          </section>
        )}

        {/* Mismo gate invertido que el botón del tab (deben moverse juntos) */}
        {activeTab === 'whatsapp-business' && cliente?.featureFlags?.embeddedSignupEnabled !== false && (
          <section className="whatsapp-business-section">
            <h2>{t('whatsappBusiness.title')}</h2>
            <p style={{ marginBottom: 16, color: '#666' }}>
              {t('whatsappBusiness.subtitle')}
            </p>

            {canManageNumbers ? (
              <WhatsAppEmbeddedSignup
                onSuccess={() => { /* onSnapshot picks up the new doc */ }}
              />
            ) : (
              <div style={{
                padding: '1rem',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '6px',
                color: '#856404',
                marginBottom: '1rem'
              }}>
                {t('permissions.noManageNumbersFull')}
              </div>
            )}

            <div className="numeros-grid" style={{ marginTop: 24 }}>
              {numerosApi.map(num => {
                const assignedBot = bots.find(b => b.numeroId === num.id);
                return (
                  <div key={num.id} className="numero-card">
                    <p>📱 {num.display_phone_number || num.numero}</p>
                    <p>{t('whatsappApi.phoneNumberId')} {num.phone_number_id}</p>
                    {num.verified_name && <p>{t('whatsappApi.name')} {num.verified_name}</p>}
                    <p>{t('whatsappWeb.status')} <span style={{
                      color: num.estado === 'conectado' ? 'green' :
                             num.estado === 'error_token' ? 'red' :
                             num.estado === 'registro_fallido' ? 'red' :
                             num.estado === 'desconectado' ? 'red' :
                             num.estado === 'en_reconexion' ? '#b45309' :
                             num.estado === 'token_expiring_soon' ? '#b45309' : 'orange',
                      fontWeight: 'bold'
                    }}>
                      {num.estado === 'conectado' ? t('whatsappApi.statusConnected') :
                       num.estado === 'error_token' ? t('whatsappApi.statusTokenError') :
                       num.estado === 'token_expiring_soon' ? t('whatsappApi.statusTokenExpiringSoon') :
                       num.estado === 'webhook_pendiente' ? t('whatsappApi.statusWebhookPending') :
                       /* Los dos estados del alta a medias (paso 6b del callback). Sin
                          estas ramas la cadena cae al `: num.estado` de abajo y le pinta
                          al cliente el literal crudo `pendiente_registro`. */
                       num.estado === 'pendiente_registro' ? t('whatsappApi.statusPendingRegister') :
                       num.estado === 'registro_fallido' ? t('whatsappApi.statusRegisterFailed') :
                       /* C4 (coexistence): lo que dejó account_update. El texto y el motivo salen de
                          textoDeNumeroNoOperativo (función pura, d74); aquí sólo se pinta. */
                       textoDeNumeroNoOperativo(num) ? t(textoDeNumeroNoOperativo(num).badge) :
                       num.estado}
                    </span></p>
                    {/* El motivo, junto al estado. Un alta que Meta rechaza deja el número
                        guardado; sin esto el cliente vería un badge rojo sin saber qué
                        hacer, y el código de Meta sólo estaría en los logs. */}
                    {num.estado === 'registro_fallido' && (
                      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: 10, margin: '8px 0', fontSize: 13, lineHeight: 1.5 }}>
                        {t('whatsappApi.registerFailedHelp')}
                        {num.registroErrorCode && (
                          <div style={{ marginTop: 6, fontFamily: 'monospace', fontSize: 12, color: '#7f1d1d' }}>
                            Meta {num.registroErrorCode}{num.registroError ? ` — ${num.registroError}` : ''}
                          </div>
                        )}
                      </div>
                    )}
                    {/* C4 (coexistence): el motivo en lenguaje simple por reason (decisión de Rodrigo,
                        27-oct), y el código de Meta debajo cuando el motivo es desconocido. */}
                    {textoDeNumeroNoOperativo(num) && (
                      <div data-testid="numero-no-operativo" style={{ background: textoDeNumeroNoOperativo(num).estado === 'desconectado' ? '#fef2f2' : '#fef3c7', border: `1px solid ${textoDeNumeroNoOperativo(num).estado === 'desconectado' ? '#fecaca' : '#fbbf24'}`, borderRadius: 6, padding: 10, margin: '8px 0', fontSize: 13, lineHeight: 1.5 }}>
                        {t(textoDeNumeroNoOperativo(num).motivo, { codigo: textoDeNumeroNoOperativo(num).codigo })}
                      </div>
                    )}
                    <BotAssignment
                      uid={uid}
                      numeroId={num.id}
                      currentBotId={assignedBot?.id}
                    />
                    <div style={{marginTop:8}}>
                      <button
                        className="delete-btn"
                        onClick={() => {
                          if (window.confirm(t('whatsappApi.confirmDelete', { number: num.display_phone_number || num.numero }))) {
                            deleteNumeroApi(num.id, num.phone_number_id);
                          }
                        }}
                      >
                        {t('whatsappWeb.deleteBtn')}
                      </button>
                    </div>
                  </div>
                );
              })}

              {numerosApi.length === 0 && (
                <div style={{
                  padding: 40,
                  textAlign: 'center',
                  color: '#999',
                  gridColumn: '1 / -1'
                }}>
                  <p>{t('whatsappBusiness.empty')}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'instagram' && (
          <section className="instagram-section">
            <div className="channel-header">
              <div className="channel-icon">📸</div>
              <div>
                <h2>{t('instagram.title')}</h2>
                <p>{t('instagram.subtitle')}</p>
              </div>
            </div>

            {/* Estado operativo = ÚNICA fuente de verdad (useChannelsOperational): badge 100%
                del helper, sin fallback al singleton (no hay verde-muerto), estado=='conectado'
                aplicado. undefined → neutro/loading; true → lista Fase 4 per-cuenta; false → no-conectado. */}
            {operational.instagram === undefined ? (
              <div className="not-connected"><p>{t('social.checkingStatus', 'Verificando estado…')}</p></div>
            ) : operational.instagram ? (
              <div className="connected">
                <div className="channel-accounts-list">
                  {igAccounts.map((account) => (
                    // Contenedor por cuenta: la fila queda INTACTA (su `display:flex` alinea
                    // icono/handle/BotAssignment/desconectar) y la sección de reglas va como
                    // hermana DEBAJO. Meterla dentro de la fila la pondría al lado, no abajo.
                    <div key={account.id} className="channel-account-block">
                    <div className="channel-account-row">
                      <div className="account-icon">📸</div>
                      <div className="account-main">
                        <div className="account-handle">@{account.instagramUsername || account.instagramUserId}</div>
                        <span className={`status-badge ${account.estado === 'conectado' ? 'success' : 'warn'}`}>
                          {account.estado === 'conectado' ? t('social.connectedStatus') : account.estado}
                        </span>
                      </div>
                      <div className="channel-account-assistant">
                        <BotAssignment
                          uid={uid}
                          integrationType="instagram"
                          accountId={account.id}
                          currentBotId={account.botId}
                        />
                      </div>
                      {canManageIntegrations && (
                        <button
                          onClick={() => handleDisconnectAccount('instagram', account, `@${account.instagramUsername || account.instagramUserId}`)}
                          disabled={disconnectingId === account.id}
                          className="disconnect-btn"
                        >
                          {disconnectingId === account.id ? t('social.disconnecting') : t('social.disconnect')}
                        </button>
                      )}
                    </div>
                    <CommentRulesSection
                      account={account}
                      bots={bots}
                      canManage={canManageIntegrations}
                    />
                    </div>
                  ))}
                </div>
                {canManageIntegrations && (
                  <div className="actions" style={{ marginTop: '1rem' }}>
                    <button onClick={handleConnectInstagram} className="connect-btn">
                      {t('social.connectAnother')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="not-connected">
                <p>{t('social.notConnected', { provider: 'Instagram' })}</p>
                {canManageIntegrations && (
                  <button onClick={handleConnectInstagram} className="connect-btn">
                    {t('social.connect', { provider: 'Instagram' })}
                  </button>
                )}
                <div className="info-box">
                  <strong>{t('social.requirements')}</strong>
                  <ul>
                    {t('social.igRequirements', { returnObjects: true }).map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </section>
        )}

        {activeTab === 'tiktok' && (
          <section className="tiktok-section">
            <div className="channel-header">
              <div className="channel-icon">🎵</div>
              <div>
                <h2>{t('tiktok.title')}</h2>
                <p>{t('tiktok.subtitle')}</p>
              </div>
            </div>

            {/* ⚠️ CORRECCIÓN (17-sep-2026). Este comentario decía que «TikTok NO es
                messaging-capable hoy (gate MESSAGING_CAPABLE) → operational.tiktok siempre
                false → no-operativo aunque el OAuth esté sano», y que la vista per-cuenta
                «se restituye al go-live». LAS DOS COSAS ERAN FALSAS y llevaban tiempo
                siéndolo: `useChannelOperational.js:18` incluye 'tiktok' en
                MESSAGING_CAPABLE, y la vista per-cuenta con BotAssignment es justo lo que
                se renderiza aquí abajo. La mensajería de TikTok se validó de punta a punta
                el 17-sep: mensaje entrante ruteado y respuesta entregada.

                Se reescribe en vez de borrarse porque un comentario que declara muerto un
                camino VIVO es peor que no tener comentario — es el texto que lleva a
                alguien a «limpiar» código que está en producción. Misma caducidad que
                `exportarChats` en CLAUDE.md: las notas describen un estado, no una
                propiedad.

                LO QUE SIGUE SIENDO CIERTO: el singleton `ttIntegration` y su profile-card
                legacy se retiraron (código muerto post-cutover), y por eso la lista de
                abajo filtra `source === 'business_messaging'`. */}
            {operational.tiktok === undefined ? (
              <div className="not-connected"><p>{t('social.checkingStatus', 'Verificando estado…')}</p></div>
            ) : operational.tiktok ? (
              <div className="connected">
                <div className="channel-accounts-list">
                  {/* Only Business Messaging accounts — filter out the legacy Login Kit
                      account (source != 'business_messaging') so it doesn't appear here. */}
                  {tiktokAccounts.filter(a => a.source === 'business_messaging').map((account) => {
                    // Display gap: the BM callback does not yet enrich name/avatar (TODO: fetch
                    // user.info.basic). Fall back to the businessId/openId for now.
                    const handle = account.username || account.accountName || account.businessId || account.openId;
                    return (
                      <div key={account.id} className="channel-account-row">
                        <div className="account-icon">🎵</div>
                        <div className="account-main">
                          <div className="account-handle">{handle}</div>
                          <span className={`status-badge ${account.estado === 'conectado' ? 'success' : 'warn'}`}>
                            {account.estado === 'conectado' ? t('social.connectedStatus') : account.estado}
                          </span>
                        </div>
                        {/* AVISO DE CUENTA, no de mensaje. El fallo de envío ya se marca
                            en la burbuja del Inbox, pero eso vive DENTRO de una
                            conversación: hay que abrirla para verlo. Un problema de
                            configuración de la cuenta afecta a todos los mensajes y se
                            arregla en TikTok, así que se muestra aquí — que es donde el
                            cliente conecta sus canales. Lo pone y lo quita el envío
                            (`bot/utils/requiereAccionTikTok.js`); desaparece solo. */}
                        {account.requiereAccion?.motivo === 'tiktok_advanced_access' && (
                          <div className="account-warning" role="alert">
                            <strong>⚠️ {t('social.requiereAccion.tiktok_advanced_access_titulo')}</strong>
                            <p>{t('social.requiereAccion.tiktok_advanced_access_pasos')}</p>
                          </div>
                        )}
                        <div className="channel-account-assistant">
                          <BotAssignment
                            uid={uid}
                            integrationType="tiktok"
                            accountId={account.id}
                            currentBotId={account.botId}
                          />
                        </div>
                        {canManageIntegrations && (
                          <button
                            onClick={() => handleDisconnectAccount('tiktok', account, handle)}
                            disabled={disconnectingId === account.id}
                            className="disconnect-btn"
                          >
                            {disconnectingId === account.id ? t('social.disconnecting') : t('social.disconnect')}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {canManageIntegrations && (
                  <div className="actions" style={{ marginTop: '1rem' }}>
                    <button onClick={handleConnectTikTok} className="connect-btn">
                      {t('social.connectAnother')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="not-connected">
                <p>{t('social.notConnected', { provider: 'TikTok' })}</p>
                {canManageIntegrations && (
                  <button onClick={handleConnectTikTok} className="connect-btn">
                    {t('social.connect', { provider: 'TikTok' })}
                  </button>
                )}
                <div className="info-box">
                  <strong>{t('social.requirements')}</strong>
                  <ul>
                    {t('social.ttRequirements', { returnObjects: true }).map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </section>
        )}

        {activeTab === 'messenger' && (
          <section className="messenger-section">
            <div className="channel-header">
              <div className="channel-icon">💬</div>
              <div>
                <h2>{t('messenger.title')}</h2>
                <p>{t('messenger.subtitle')}</p>
              </div>
            </div>

            {/* Estado operativo = helper (badge 100% del helper, sin fallback al singleton →
                sin verde-muerto, estado=='conectado' aplicado). undefined → neutro; true → lista
                Fase 4 per-cuenta; false → no-conectado. */}
            {operational.messenger === undefined ? (
              <div className="not-connected"><p>{t('social.checkingStatus', 'Verificando estado…')}</p></div>
            ) : operational.messenger ? (
              <div className="connected">
                <div className="channel-accounts-list">
                  {messengerAccounts.map((account) => (
                    <div key={account.id} className="channel-account-row">
                      <div className="account-icon">💬</div>
                      <div className="account-main">
                        <div className="account-handle">{account.pageName || account.pageId}</div>
                        <span className={`status-badge ${account.estado === 'conectado' ? 'success' : 'warn'}`}>
                          {account.estado === 'conectado' ? t('social.connectedStatus') : account.estado}
                        </span>
                      </div>
                      <div className="channel-account-assistant">
                        <BotAssignment
                          uid={uid}
                          integrationType="messenger"
                          accountId={account.id}
                          currentBotId={account.botId}
                        />
                      </div>
                      {canManageIntegrations && (
                        <button
                          onClick={() => handleDisconnectAccount('messenger', account, account.pageName || account.pageId)}
                          disabled={disconnectingId === account.id}
                          className="disconnect-btn"
                        >
                          {disconnectingId === account.id ? t('social.disconnecting') : t('social.disconnect')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {canManageIntegrations && (
                  <div className="actions" style={{ marginTop: '1rem' }}>
                    {/* Facebook Login for Business (config_id) en vez del redirect
                        con scope=. El token que emite NO expira; el del flujo
                        anterior caducaba a los 60 días y apagaba el canal en
                        silencio, sin aviso.

                        REVERSIÓN: reemplazar este componente por
                        <button onClick={handleConnectMessenger} className="connect-btn">
                        y desplegar hosting. `handleConnectMessenger`,
                        `messengerStart.js` y `integrations_messenger_callback`
                        siguen intactos a propósito — la vuelta atrás no toca
                        functions ni desconecta lo que ya está conectado. */}
                    <MessengerBusinessLogin organizationId={orgId} />
                    {orgId === ORG_DUENA_DE_LA_APP && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <button
                          onClick={handleConnectMessenger}
                          style={{ background: 'none', border: 'none', padding: 0, color: '#718096',
                                   fontSize: 13, textDecoration: 'underline', cursor: 'pointer' }}
                          title="Sólo para páginas del portfolio dueño de la app, que Login for Business no permite seleccionar"
                        >
                          Conectar por flujo clásico
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="not-connected">
                <p>{t('social.notConnected', { provider: 'Messenger' })}</p>
                {canManageIntegrations && (
                  <>
                    <MessengerBusinessLogin organizationId={orgId} />
                    {orgId === ORG_DUENA_DE_LA_APP && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <button
                          onClick={handleConnectMessenger}
                          style={{ background: 'none', border: 'none', padding: 0, color: '#718096',
                                   fontSize: 13, textDecoration: 'underline', cursor: 'pointer' }}
                          title="Sólo para páginas del portfolio dueño de la app, que Login for Business no permite seleccionar"
                        >
                          Conectar por flujo clásico
                        </button>
                      </div>
                  )}
                  </>
                )}
                <div className="info-box">
                  <strong>{t('social.requirements')}</strong>
                  <ul>
                    {t('social.msRequirements', { returnObjects: true }).map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
